import { IProfile } from '@comptia-sso/core';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
const ORIGINS_KEY = '_framed_host_origins';
const ALL_FRAMES_KEY = '_framed_host_allFrames';
const CURRENT_FRAME_KEY = '_framed_host_currentFrame';
const SWAPCALLBACKS_KEY = '_innovation_frameSwapCallbacks';
const INITFRAMECALLBACKS_KEY = '_innovation_initFrameCallbacks';
(window as any)[ORIGINS_KEY] = [
  'https://cedev.comptia.org',
  'https://ce.comptia.org',
];
(window as any)[ALL_FRAMES_KEY] = [];
(window as any)[CURRENT_FRAME_KEY] = '';
function isSafeOrigin(origin: string) {
  const origins = (window as any)[ORIGINS_KEY] as string[];
  if (origins.indexOf(origin.toLowerCase()) === -1) return false;
  return true;
}
interface IMessageData {
  frameId: string;
  command: string;
  payload: string;
}
function receiveMessage(event: any) {
  if (!isSafeOrigin(event.origin)) return;
  if (typeof event.data === 'string') {
    const messageData = JSON.parse(event.data) as IMessageData;
    const iframe = getFrame(messageData.frameId);
    switch (messageData.command) {
      case 'INIT': {
        iframe.dataset.initialized = 'true';
        iframe.dataset.initPayload = JSON.stringify(messageData.payload);
        pushMessages(messageData.frameId);
        doCallbacks(getInitFrameCallbacks());
        if (isForceWindowed()) {
          const onloadcallback = (iframe as any).onloadcallback as () => void;
          if (onloadcallback != null) onloadcallback();
        }
        break;
      }
      case 'CUSTOM_COMMAND': {
        const cb = (iframe as any).customcommandcallback;
        if (cb != null) {
          const cmd = JSON.parse(messageData.payload);
          cb(cmd);
        }
        break;
      }
    }
  }
}
window.addEventListener('message', receiveMessage, false);
function getFramedWindow(iframe: HTMLIFrameElement) {
  const windowed = (iframe as any).windowed;
  return windowed ?? iframe.contentWindow;
}
function queueMessage(iframeId: string, message: string) {
  const iframe = getFrame(iframeId);
  if (iframe.dataset.initialized === 'true')
    getFramedWindow(iframe)?.postMessage(
      message,
      iframe.dataset.origin as string,
    );
  else {
    const messages = JSON.parse(iframe.dataset.outbox ?? '[]') as string[];
    messages.push(message);
    iframe.dataset.outbox = JSON.stringify(messages);
  }
}
function pushMessages(iframeId: string) {
  const iframe = getFrame(iframeId);
  if (iframe.dataset.initialized === 'true') {
    const messages = JSON.parse(iframe.dataset.outbox ?? '[]') as string[];
    for (let i = 0; i < messages.length; i++)
      getFramedWindow(iframe)?.postMessage(
        messages[i],
        iframe.dataset.origin as string,
      );
    delete iframe.dataset.outbox;
  }
}
function isForceWindowed() {
  const isLocalhost = window.location.hostname === 'localhost';
  const searchParams = new URLSearchParams(window.location.search);
  const hasWindowedQuery = searchParams.has('windowed');
  return isLocalhost && hasWindowedQuery;
}
export interface IMenuItem {
  title: string;
  link: string;
}
export interface IFramedMenuItem extends IMenuItem {
  frameId: string;
}
function createFrame(
  frameId: string,
  url: string,
  cssClassName: string,
  onloadCallback?: () => void,
  customCommandHandler?: (cmd: any) => void,
) {
  const origins = (window as any)[ORIGINS_KEY] as string[];
  const urlObj = new URL(url);
  const origin = urlObj.origin.toLowerCase();
  urlObj.searchParams.append('host_frameid', frameId);
  if (origins.indexOf(origin) === -1) origins.push(origin);
  const iframe = document.createElement('iframe');
  iframe.id = frameId;
  iframe.dataset.origin = origin;
  iframe.className = cssClassName;
  iframe.style.display = 'none';

  iframe.style.border = 'none';
  iframe.style.position = 'absolute';
  iframe.style.left = '0px';
  iframe.style.top = '70px';
  iframe.style.width = '100%';
  iframe.style.height = 'calc(100vh - 100px)';
  iframe.style.background = 'white';
  (iframe as any).customcommandcallback = customCommandHandler;

  document.body.appendChild(iframe);
  if (isForceWindowed()) {
    const wnd = window.open(urlObj.toString());
    if (wnd != null) {
      (iframe as any).windowed = wnd;
      (iframe as any).onloadcallback = onloadCallback;
    }
  } else {
    iframe.onload = () => {
      if (onloadCallback != null) onloadCallback();
    };
    iframe.src = urlObj.toString();
  }
  (window as any)[ALL_FRAMES_KEY].push(iframe);
}
export function destroyFrame(frameId: string) {
  const iframe = getFrame(frameId);
  if (iframe != null) {
    document.body.removeChild(iframe);
  }
}
export function getFrame(frameId: string): HTMLIFrameElement {
  return document.getElementById(frameId) as HTMLIFrameElement;
}
export function getFrameMenu(): Array<IFramedMenuItem> {
  const allFrames = (window as any)[ALL_FRAMES_KEY] as Array<HTMLIFrameElement>;
  let menu = [] as Array<IFramedMenuItem>;
  for (let i = 0; i < allFrames.length; i++) {
    const frame = allFrames[i];
    const payload = frame.dataset.initPayload;
    if (payload != null && payload.length > 0) {
      const obj = JSON.parse(payload);
      const addItems = obj.menu as Array<any>;
      for (let j = 0; j < addItems.length; j++) addItems[j].frameId = frame.id;
      menu = menu.concat(addItems);
    }
  }
  return menu;
}
function getSwapCallbacks() {
  let callbacks = (window as any)[SWAPCALLBACKS_KEY] as Array<() => void>;
  if (callbacks == null) {
    callbacks = [];
    (window as any)[SWAPCALLBACKS_KEY] = callbacks;
  }
  return callbacks;
}
export function registerFrameSwapCallback(cb: () => void) {
  getSwapCallbacks().push(cb);
}
export function unRegisterFrameSwapCallback(cb: () => void) {
  const cbs = getSwapCallbacks();
  const idx = cbs.indexOf(cb);
  if (idx !== -1) {
    cbs.splice(idx, 1);
  }
}
function getInitFrameCallbacks() {
  let callbacks = (window as any)[INITFRAMECALLBACKS_KEY] as Array<() => void>;
  if (callbacks == null) {
    callbacks = [];
    (window as any)[INITFRAMECALLBACKS_KEY] = callbacks;
  }
  return callbacks;
}
export function registerInitFrameCallback(cb: () => void) {
  getInitFrameCallbacks().push(cb);
}
export function unRegisterInitFrameCallback(cb: () => void) {
  const cbs = getInitFrameCallbacks();
  const idx = cbs.indexOf(cb);
  if (idx !== -1) {
    cbs.splice(idx, 1);
  }
}
export function frameHide(frameId: string) {
  const iframe = getFrame(frameId);
  if (iframe != null) iframe.style.display = 'none';
}
export function getFrameCount() {
  return ((window as any)[ALL_FRAMES_KEY] as Array<HTMLIFrameElement>).length;
}
export function getCurrentFrameId() {
  return (window as any)[CURRENT_FRAME_KEY] ?? '';
}
function doCallbacks(cbs: Array<() => void>) {
  // Cloning the array and using a for loop instead of array.forEach ensures
  // all the callbacks will get called now regardless of changes to the array
  const cloned = [...cbs];
  for (let i = 0; i < cloned.length; i++) {
    try {
      cloned[i]();
    } catch {}
  }
}
export function frameShow(frameId: string) {
  const iframe = getFrame(frameId);
  if (iframe != null) iframe.style.display = 'block';
  (window as any)[CURRENT_FRAME_KEY] = frameId;
  doCallbacks(getSwapCallbacks());
}
export function frameNavigate(frameId: string, path: string) {
  const iframe = getFrame(frameId);
  if (iframe != null)
    getFramedWindow(iframe)?.postMessage(
      JSON.stringify({ command: 'NAV', payload: path }),
      iframe.dataset.origin as string,
    );
}
export function frameSendAuthToken(frameId: string, token: string) {
  queueMessage(
    frameId,
    JSON.stringify({ command: 'AUTHTOKEN', payload: btoa(token) }),
  );
}
export interface IHostedRouterSyncProps {
  frameId: string;
}
export function useHostedRouterSync(props: IHostedRouterSyncProps) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.pathname.startsWith(`/${props.frameId}`))
      frameShow(props.frameId);
    else frameHide(props.frameId);
  }, [location]);
  useEffect(() => {
    function receiveMessage(event: any) {
      if (!isSafeOrigin(event.origin)) return;
      const messageData = JSON.parse(event.data);
      switch (messageData.command) {
        case 'NAV': {
          const currentPath = window.location.pathname;
          let childUrl = messageData.payload;
          if (childUrl === '') childUrl = '/';
          const newHostPath = `/${props.frameId}${
            childUrl === '/' ? '' : childUrl
          }`;
          if (
            currentPath.startsWith(`/${props.frameId}`) &&
            currentPath !== newHostPath
          ) {
            navigate(newHostPath, { replace: true });
          }
          break;
        }
      }
    }
    window.addEventListener('message', receiveMessage, false);
    return () => {
      window.removeEventListener('message', receiveMessage, false);
    };
  }, [navigate]);
}

const sendMessageToChild = (frameId: string, authToken: string) => {
  const iframe = getFrame(frameId);
  if (iframe && iframe.contentWindow) {
    console.log('Sending message to child iframe');
    const authTokenMessage = {
      command: 'AUTHTOKEN',
      payload: btoa(authToken),
    };
    getFramedWindow(iframe)?.postMessage(
      authTokenMessage,
      iframe.dataset.origin as string,
    );
  } else {
    console.warn('Iframe not found or not loaded');
  }
};

export function createFrameForProfile(
  profile: IProfile,
  frameId: string,
  url: string,
  customCommandHandler: (cmd: any) => void,
) {
  const iframeWebToken = profile?.iframeWebToken ?? '';
  if (iframeWebToken.length > 0) {
    createFrame(
      frameId,
      url,
      'hosted_iframe',
      () => {
        setTimeout(() => {
          sendMessageToChild(frameId, iframeWebToken);
        }, 1000);
      },
      customCommandHandler,
    );
  }
}

export function allFramesInitialized() {
  const frames = (window as any)[ALL_FRAMES_KEY] as any;
  let initialized = frames.length > 0;
  for (let idx = 0; idx < frames.length; idx++) {
    initialized = initialized && frames[idx].dataset.initialized === 'true';
    if (initialized) {
      const messages = JSON.parse(
        frames[idx].dataset.outbox ?? '[]',
      ) as string[];
      initialized = initialized && messages.length === 0;
    }
  }
  return initialized;
}
